import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { StoreInterface } from '../store/types';
import { Helmet } from 'react-helmet';
import { OpeningHoursInterface } from '../services/exports/Interfaces';
import collect from 'collect.js';
import moment from 'moment';
import AddressHelpers from '../services/helpers/AddressHelpers';
import useConfig from '../hooks/ui/useConfig';
import { SCREENS } from '../services/exports/Constants';
import useMenu from '../hooks/menu/useMenu';
import { useGetPageQuery } from '../store/api-slices';
import useCompanySlug from '../hooks/global/useCompanySlug';
import { getStorefrontBasePath } from '../modules/storefront/exports/Cookies';
import useStorefront from '../hooks/storefront/useStorefront';
import { toJsonLd } from '../services/helpers/StructuredData';
import { toRunnableHeadScript } from '../services/helpers/RestaurantHeadScript';

export default function MetaInfo() {
  const { t } = useTranslation(null, { keyPrefix: 'Components:MetaInfo' });

  const { company, franchise } = useSelector((state: StoreInterface) => state.initialData);
  const { categories } = useMenu();
  const { logo_url, favicon_url } = useConfig();
  const { slug } = useCompanySlug();
  const companySlug = slug || company?.slug;
  const { isStorefront } = useStorefront();
  const { data: page } = useGetPageQuery(`${getStorefrontBasePath()}/${companySlug}`, {
    skip: !companySlug || !isStorefront,
  });

  const logo = useMemo(() => {
    if (page?.seo?.image) return page.seo.image.url;

    return logo_url;
  }, [logo_url, page]);

  const title: string = useMemo(() => {
    if (franchise?.seo_data?.title) return franchise.seo_data.title;

    if (page?.seo?.title) return page.seo.title;

    return company?.name
      ? t('seo.title', {
          name: company?.name,
          city: company?.city,
        })
      : t('default_browser_tag');
  }, [company, page, franchise]);

  const description: string = useMemo(() => {
    if (franchise?.seo_data?.description) return franchise.seo_data.description;

    if (page?.seo) return page.seo.description;

    return t('seo.description', {
      name: company?.name,
      city: company?.city,
    });
  }, [company, page, franchise]);

  const address = AddressHelpers.format({
    street_name: company?.address,
    city: company?.city,
    zip_code: company?.zip_code,
    format: company?.region?.address_format,
  });

  const schema = useMemo(() => {
    const menu = {
      '@type': 'Menu',
      name: 'Our Menu',
      hasMenuSection: collect(categories)
        .map((category) => ({
          '@type': 'MenuSection',
          name: category?.name,
          hasMenuItem: collect(category?.products)
            .map((product) => ({
              '@type': 'MenuItem',
              name: product?.name,
              description: product?.description,
              image: product?.thumbnail_url,
              price: product?.base_price,
              priceCurrency: typeof company?.currency === 'string' ? company?.currency?.toUpperCase() : null,
            }))
            .toArray(),
        }))
        .toArray(),
    };

    const formatHour = (hour: number, minute: number): string => {
      return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}:00`;
    };

    const openingHoursSpecification = collect(company?.work_schedule)
      ?.map((openingHour: OpeningHoursInterface) => {
        return {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: `https://schema.org/${moment().locale('en').weekday(openingHour?.open?.day).format('dddd')}`,
          opens: formatHour(openingHour?.open?.hour, openingHour?.open?.minute),
          closes: formatHour(openingHour?.close?.hour, openingHour?.close?.minute),
        };
      })
      .toArray();

    return {
      '@context': 'http://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          name: 'Parqet',
          url: 'https://www.parqet.com/',
          logo: 'https://www.parqet.com/logos/parqet_logo_pos.svg',
        },
        ...(company?.id
          ? [
              {
                '@type': 'Restaurant',
                name: company.name,
                legalName: company.name,
                description,
                url: window.location.href,
                image: collect(company.pictures).first(),
                address: {
                  '@type': 'PostalAddress',
                  addressLocality: company.city,
                  postalCode: company.zip_code,
                  streetAddress: address,
                },
                keywords: [company.name, company.city, 'order online'],
                logo,
                telephone: company.phone_number,
                email: company.email,
                latitude: company.lat,
                longitude: company.lon,
                hasMenu: menu,
                openingHoursSpecification,
              },
            ]
          : []),
      ],
    };
  }, [company, categories, logo, description]);

  function setNoScript(): void {
    const noScriptId = 'body-noscript';

    const existingNoscript = document.getElementById(noScriptId);

    if (!franchise?.body_noscripts && existingNoscript) {
      document.body.removeChild(existingNoscript);

      return;
    }

    if (!franchise?.body_noscripts) {
      return;
    }

    if (existingNoscript) {
      existingNoscript.innerHTML = franchise?.body_noscripts;

      return;
    }

    const noscript = document.createElement('noscript');
    noscript.id = noScriptId;
    noscript.innerHTML = franchise?.body_noscripts;
    document.body.insertBefore(noscript, document.body.firstChild);
  }

  useEffect(() => {
    setNoScript();
  }, [franchise?.body_noscripts]);

  const headScript = useMemo(() => toRunnableHeadScript(franchise?.head_scripts), [franchise?.head_scripts]);

  const noIndex = useMemo(
    () =>
      [SCREENS.COOKIE_POLICY, SCREENS.IMPRESSUM, SCREENS.PRIVACY_POLICY, SCREENS.TERMS_AND_CONDITIONS].some((screen) =>
        window.location.href.includes(screen),
      ) && <meta name="robots" content="noindex, nofollow" />,
    [window.location.href],
  );

  return (
    <div className="application">
      <Helmet>
        <meta charSet="utf-8" />
        <link rel="icon" href={favicon_url ?? logo_url} />
        <title>{title}</title>
        {description && <meta name="description" content={description} />}
        <meta property="og:site_name" content={company?.name} />
        <meta property="og:title" content={title} />
        {description && <meta property="og:description" content={description} />}
        <meta property="og:image" content={logo} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@FoodAmigos" />
        <meta name="twitter:creator" content="@FoodAmigos" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={logo} />
        {noIndex}
        <script type="application/ld+json">{toJsonLd(schema)}</script>
        {headScript && (
          <script type="text/javascript" id="head-script">
            {headScript}
          </script>
        )}
      </Helmet>
    </div>
  );
}
